import { Box, Chip, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import {
  Button,
  DatagridConfigurable,
  FilterButton,
  FilterLiveSearch,
  List,
  NumberField,
  FunctionField,
  NumberInput,
  TopToolbar,
  useCreate,
  useListContext,
  useNotify,
  useRedirect,
  TextField,
  ReferenceArrayInput,
  AutocompleteArrayInput,
  AutocompleteInput,
  ReferenceInput,
  SelectInput,
  SelectArrayInput,
  useGetList,
} from "react-admin";
import CustomPagination from "../Componentes/CustomPagination";
import { MyUrlField } from "../Componentes/MyUrlField";
import MyUrlFieldErp from "../Componentes/MyUrlFieldErp";
import { SkuRankingsEmpty } from "./SkuRankingsEmpty";
import { getSituacaoCellSx } from "./situacaoStyles";
import { ScmSkus } from "../types";

type SkuRankingRecord = {
  situacao?: string | null;
};

interface QuickFilterProps {
  label: string;
  source?: string;
  defaultValue?: boolean;
}

const QuickFilter: React.FC<QuickFilterProps> = ({ label }) => (
  <Chip sx={{ marginBottom: 1 }} label={label} />
);

const scmSkuRowSx = (record: ScmSkus & { sku_ativo?: boolean }) =>
  record.sku_ativo === true ? {} : { backgroundColor: "lightgray" };

const optionSkuGroupsRenderer = (choice: any) =>
  `${choice.codigo_agrup}. ${choice.desc_agrupamento}`;

const optionFornecedorRenderer = (choice: any) =>
  `${choice?.nome_fornec} ${typeof choice?.quant_skus !== "undefined" ? "(" + choice?.quant_skus + " SKUs)" : ""}`;

const optionCategoriesRenderer = (choice: any) => {
  return choice
    ? `${choice.id !== null ? choice.id + ". " : ""} ${
        typeof choice.breadcrumb !== "undefined" ? choice.breadcrumb : ""
      }`
    : "";
};

const filters = [
  <FilterLiveSearch source="q" label="Cód Produto/Descrição" alwaysOn />,
  <ReferenceArrayInput
    source="ids_tags"
    reference="producttags"
    alwaysOn
    sort={{ field: "nome_tag", order: "ASC" }}
    perPage={100}
  >
    <AutocompleteArrayInput
      label="Tags"
      // optionText="nome_fornec"
      optionText="nome_tag"
      filterToQuery={(nome_tag) => ({ nome_tag })}
      // helperText={false}
      sx={{ width: "400px" }}
    />
  </ReferenceArrayInput>,
  <ReferenceInput source="id_fornec" reference="suppliers" alwaysOn>
    <AutocompleteInput
      label="Fornecedor"
      // optionText="nome_fornec"
      optionText={optionFornecedorRenderer}
      filterToQuery={(pesquisa) => ({ pesquisa })}
      // helperText={false}
      sx={{ width: "400px" }}
    />
  </ReferenceInput>,
  // #####################################  <QuickFilter source="sem_cmm" label="Sem CMM" defaultValue={true} />,
  <QuickFilter source="todos" label="Todos" defaultValue={true} />,
  <ReferenceArrayInput
    resource="products"
    source="ids_acs_excluir"
    label="ACs a Excluir"
    reference="skugroups"
    perPage={100}
    sort={{ field: "id", order: "ASC" }}
    InputLabelProps={{ shrink: true }}
  >
    <SelectArrayInput
      optionText={optionSkuGroupsRenderer}
      style={{ width: "250px" }}
    />
  </ReferenceArrayInput>,
  <ReferenceInput
    source="id_comprador"
    reference="buyers"
    perPage={100}
    sort={{ field: "id", order: "ASC" }}
    fullWidth
    label="Comprador"
  >
    <SelectInput optionText="nome_razao" label="Comprador" />
  </ReferenceInput>,
  <NumberInput source="curva_minima" label="Curva Min" />,
  <NumberInput source="curva_maxima" label="Curva Max" />,
  <NumberInput source="id" label="ID" />,
  <QuickFilter
    label="Incluir Inativos"
    source="incluir_inativos"
    defaultValue={true}
  />,
  <NumberInput source="numero_pedido" label="Pedido" />,
  // <TextInput source="taxonomia" />,
  <ReferenceInput
    label="Taxonomia"
    reference="categories"
    resource="products"
    source="id_categoria"
    style={{ width: "450px" }}
    perPage={9999}
    sort={{ field: "breadcrumb", order: "ASC" }}
    //fullWidth
    // filter={{ dump: true }}
    resettable
  >
    <AutocompleteInput
      source="id"
      optionText={optionCategoriesRenderer}
      label="Taxonomia"
      style={{ width: "450px" }}
    />
  </ReferenceInput>,
];

const SkuRankingsActions = () => (
  <TopToolbar>
    <FilterButton disableSaveQuery />
  </TopToolbar>
);

const CriaAnalise = () => {
  const { selectedIds, filterValues } = useListContext();
  const [create] = useCreate();
  const notify = useNotify();
  const redirect = useRedirect();

  const handleCriarAnalise = () => {
    if (!selectedIds.length) {
      notify("Nenhum item selecionado.", { type: "warning" });
      return;
    }

    const payload = {
      skus: selectedIds,
      filtros_analise: filterValues,
    };

    create(
      "scmanalyses",
      { data: payload },
      {
        onSuccess: (response: { id?: string | number }) => {
          notify("Análise criada com sucesso!", { type: "success" });
          if (response?.id != null) {
            redirect("edit", "scmanalyses", response.id);
          }
        },
        onError: () => {
          notify("Erro ao criar a análise.", { type: "error" });
        },
      },
    );
  };

  return (
    <Button
      size="small"
      color="primary"
      onClick={handleCriarAnalise}
      label="Criar Análise"
    >
      <AnalyticsIcon />
    </Button>
  );
};

export function SkuRankingsList() {
  // Para controlar o filtro default da tela de compras nos tags
  const { data } = useGetList("producttags", {
    filter: { padrao_compra: true },
    pagination: { perPage: 9999, page: 1 },
    sort: { field: "nome_tag", order: "ASC" },
    // pagination:{perPage:  }
  });
  const [selectedIds, setSelectedIds] = useState<any[]>([]);
  useEffect(() => {
    if (data && selectedIds.length === 0) {
      const defaultIds = data.map((item) => item.id); // Define os valores iniciais com base nos dados
      setSelectedIds(defaultIds); // Atualiza o estado com os IDs padrão
    }
  }, [data]);

  return (
    <Stack gap={4}>
      <List
        storeKey={false}
        disableSyncWithLocation
        filters={filters}
        actions={<SkuRankingsActions />}
        sort={{ field: "dos", order: "ASC" }}
        perPage={50}
        pagination={<CustomPagination />}
        empty={false}
        filterDefaultValues={{
          ids_tags: selectedIds,
        }}
      >
        <DatagridConfigurable
          bulkActionButtons={<CriaAnalise />}
          empty={<SkuRankingsEmpty />}
        >
          <NumberField source="id" options={{ useGrouping: false }} />
          <FunctionField<ScmSkus>
            label="Produto/Taxonomia"
            sortBy="id_categoria"
            render={(record) => (
              <div>
                <TextField source="prod_xprod" />
                <div style={{ fontSize: "smaller" }}>
                  <NumberField
                    source="id_categoria"
                    options={{ useGrouping: false }}
                    sx={{ fontSize: "x-small" }}
                  />
                  {record?.taxonomia ? ". " : ""}
                  <TextField source="taxonomia" sx={{ fontSize: "x-small" }} />
                </div>
              </div>
            )}
          />
          {/* <MyUrlFieldErp recurso="products" source="prod_xprod" alvo="id" /> */}
          {/* <MyUrlField
            recurso="suppliers"
            source="nome_fornec"
            alvo="id_fornec"
          /> */}
          <FunctionField<ScmSkus>
            label="Fornecedor/Comprador"
            sortBy="nome_fornec"
            render={(record) => (
              <div>
                <NumberField
                  source="id_fornec"
                  options={{ useGrouping: false }}
                />
                {". "}
                <TextField source="nome_fornec" />
                <div style={{ fontSize: "smaller" }}>
                  <TextField
                    source="nome_comprador"
                    sx={{ fontSize: "x-small" }}
                  />
                </div>
              </div>
            )}
          />
          <TextField source="nome_marca" label="Marca" />
          <TextField source="codigo_agrup" label="AC" />
          <NumberField source="er" options={{ useGrouping: false }} />
          <NumberField
            source="ev"
            label="EV"
            options={{ useGrouping: false }}
          />
          <NumberField
            source="nr"
            label="NR"
            options={{ useGrouping: false }}
          />
          <NumberField
            source="dnrg"
            label="DNRG"
            options={{ useGrouping: false }}
          />
          <FunctionField<SkuRankingRecord>
            source="situacao"
            label="Situação"
            render={(record) => (
              <Box
                component="span"
                sx={{
                  display: "block",
                  width: "100%",
                  textAlign: "center",
                  py: 0.25,
                  ...getSituacaoCellSx(record?.situacao),
                }}
              >
                {record?.situacao ?? ""}
              </Box>
            )}
          />
          <NumberField
            source="dos"
            label="DOS"
            options={{ useGrouping: false }}
          />
        </DatagridConfigurable>
      </List>
    </Stack>
  );
}
